/**
 * useExplicitContentGate
 *
 * Sibling to `useContentPlaybackTracker` — owns the NSFW (explicit content)
 * gate. While gated, the video is held paused; tapping the reveal CTA clears
 * the gate and resumes playback.
 *
 * Mirrors Flutter's:
 *   - lib/presentation/view_models/content_view_model.dart:155-176 (initial
 *     state derivation in `setContentInfo`)
 *   - lib/presentation/view_models/content_view_model.dart:178-182
 *     (`showExplicitContent` reveal flow with `forcePlayIfMarkPaused: true`)
 *   - lib/presentation/view_models/content_view_model.dart:400-404
 *     (`_checkExplicitContentUpdates` periodic auto-pause while gated)
 *
 * Web adaptation: rather than re-checking on every playback tick, we attach a
 * one-time `play` listener on the video element and pause synchronously when
 * a play attempt fires while gated. Owner detection comes from the caller
 * (already computed in ContentComponent / ContentComponentFullScreenSupport).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/presentation/store';
import { requestPause } from '@/presentation/store/slices/videoManagement/videoManagementSlice';
import { videoElementRegistry } from '@/presentation/registries/videoElementRegistry';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';

interface UseExplicitContentGateParams {
  videoKey: string;
  /** Media entity backing the video — needed for the init-on-reveal path. */
  media: UserMediaEntity;
  /** True when the current user is the content owner (gate never fires for owners). */
  isOwner: boolean;
  /** Whether `media.explicit` exceeds Flutter's allowed pornography likelihood. */
  isNsfw: boolean;
}

export function useExplicitContentGate({
  videoKey,
  media,
  isOwner,
  isNsfw,
}: UseExplicitContentGateParams): { gated: boolean; reveal: () => void } {
  const dispatch = useDispatch<AppDispatch>();
  const isInitialized = useSelector<RootState, boolean>((state) => {
    const v = state.video.videos[videoKey];
    return !!(v && !v.isLoading && !v.error && !v.isDisposed && v.initializedAt > 0);
  });
  const { initializeVideo, playVideo } = useVideoManagement();
  const initializeVideoRef = useRef(initializeVideo);
  initializeVideoRef.current = initializeVideo;
  const playVideoRef = useRef(playVideo);
  playVideoRef.current = playVideo;

  const initialGated = isNsfw && !isOwner;
  const [gated, setGated] = useState(initialGated);
  // Ref mirrors `gated` synchronously so the play listener and reveal flow
  // can flip the gate before React's next render cycle runs (otherwise
  // `videoEl.play()` inside reveal would race against an in-flight 'play'
  // event still seeing the old listener closure).
  const gatedRef = useRef(initialGated);

  // Mirror prop changes (e.g. media swaps in feed pager) into local state.
  // Once revealed for a given (videoKey, isNsfw, isOwner) tuple we stay
  // revealed — only re-gate when the gating inputs flip back to true.
  const lastSignatureRef = useRef<string>(`${videoKey}|${isNsfw}|${isOwner}`);
  useEffect(() => {
    const sig = `${videoKey}|${isNsfw}|${isOwner}`;
    if (sig !== lastSignatureRef.current) {
      lastSignatureRef.current = sig;
      const next = isNsfw && !isOwner;
      gatedRef.current = next;
      setGated(next);
    }
  }, [videoKey, isNsfw, isOwner]);

  // Pause-while-gated: pause synchronously on any `play` attempt, plus an
  // initial sweep in case the video is already playing when the gate becomes
  // active (e.g. autoplay started before this hook attached).
  //
  // Attaches as soon as the video element is registered (well before the first
  // `playing` event), avoiding an autoplay race where audio leaked through the
  // blur for tens of ms while we waited on `onPlaybackStart` to flip a ready
  // flag. EncryptedVideoPlayer registers the element in a child useEffect, so
  // by the time this parent effect runs the registry already has it.
  useEffect(() => {
    if (!gated) return;

    const videoEl = videoElementRegistry.get(videoKey);
    if (!videoEl) return;

    // We can't use `useVideoManagement.pauseVideo` here: it short-circuits
    // when Redux's `playbackStates[videoKey].isPlaying` is false, but that
    // flag is updated via VideoComponent's local-state → useEffect →
    // `updatePlaybackState` chain, which is async relative to the native
    // 'play' event. Worse, plays from autoplay or browser controls never
    // dispatch our requestPlay at all. A direct `videoEl.pause()` plus a
    // forced requestPause is the only path that works for every play
    // origin. The forced flag is what `autoTogglePlayPauseActiveVideo` reads
    // to skip auto-resume on navigation.
    const pauseAndForce = () => {
      if (!gatedRef.current) return;
      videoEl.pause();
      dispatch(requestPause({ videoKey, forced: true }));
    };

    if (!videoEl.paused) pauseAndForce();
    videoEl.addEventListener('play', pauseAndForce);

    return () => {
      videoEl.removeEventListener('play', pauseAndForce);
    };
  }, [gated, videoKey, dispatch]);

  const reveal = useCallback(() => {
    // Flip the ref *before* calling play() so the still-attached play
    // listener bails out instead of immediately re-pausing.
    gatedRef.current = false;
    setGated(false);

    // Mirrors Flutter's `_playVideo(forcePlayIfMarkPaused: true)`. Flutter's
    // `_playVideo` also performs an init if the controller hasn't been built
    // yet — on web, init is a separate Redux thunk that is normally fired
    // by `ContentListItem.onClick`. If the user reveals before tapping the
    // video (autoplay=false / cold load), kick off init here so reveal is a
    // single-action flow instead of needing a second tap.
    if (!isInitialized) {
      void initializeVideoRef.current(media, videoKey, true, { disposePrevious: true });
      return;
    }

    // Already initialized — play through the standard surface. Safe to use
    // the wrapper here because the gate has been holding the video paused,
    // so Redux's `isPlaying` is reliably false and `playVideo`'s guard
    // passes.
    void playVideoRef.current(videoKey).catch(() => {
      // play() can reject if the user gesture didn't propagate. The user
      // can tap the play button to retry.
    });
  }, [isInitialized, videoKey, media]);

  return { gated, reveal };
}
